import React, { useCallback, useEffect, useState, useRef } from "react";
import axiosInstance from "../services/axiosinstance";
import { apiUrl } from "../routes/apiRoutes";
import {
  Loader2,
  ToggleLeft,
  ToggleRight,
  Plus,
  EyeIcon,
  ImagePlus,
  X,
  Pencil,
  Trash2,
} from "lucide-react";
import { Link, useNavigate } from "react-router";

const defaultForm = {
  name: "",
  email: "",
  password: "",
  number: "",
  experience: "",
  subjects: "",
  instructorPhoto: null,
};

function FacultyManagement() {
  const navigate = useNavigate();
  const [instructors, setInstructors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState(null); // null = add mode, object = edit mode
  const fileInputRef = useRef(null);
  const controller = new AbortController();

  const fetchInstructors = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get(apiUrl.GET_INSTRUCTORS, {
        signal: controller.signal,
      });
      if (res.data?.status && Array.isArray(res.data?.data)) {
        setInstructors(res.data.data);
      } else {
        setInstructors([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load instructors");
      setInstructors([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInstructors();
    return () => controller.abort();
  }, [fetchInstructors]);

  // ── open edit form pre-filled ──
  const handleEditClick = (inst) => {
    // console.log("details", inst);
    setEditingInstructor(inst);
    setForm({
      name: inst.name || "",
      email: inst.email || "",
      password: "", // never pre-fill password
      number: inst.number || "",
      experience: inst.experience || "",
      subjects: Array.isArray(inst.subjects) ? inst.subjects.join(", ") : "",
      instructorPhoto: null,
    });
    setPhotoPreview(inst?.profile || null); // show existing photo if URL available
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteClick = async (id) => {
    if (!window.confirm("sure to remove the faculty details?")) {
      return;
    }
    try {
      const deleted = await axiosInstance.delete(apiUrl.DELETE_INSTRUCTOR(id));
      alert(deleted.data.message);
    } catch (error) {
      alert(`error: ${error.response.data.message}`);
    }
  };

  const handleAddClick = () => {
    if (showForm && !editingInstructor) {
      // toggling off add-mode form
      setShowForm(false);
      return;
    }
    setEditingInstructor(null);
    setForm(defaultForm);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    setShowForm(true);
  };

  const handleToggleActive = async (instructor) => {
    setSavingId(instructor._id);
    try {
      await axiosInstance.patch(`${apiUrl.GET_INSTRUCTORS}/${instructor._id}`, {
        isActive: !instructor.isActive,
      });
      await fetchInstructors();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to update instructor");
    } finally {
      setSavingId(null);
    }
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setForm((prev) => ({ ...prev, instructorPhoto: file }));
    const reader = new FileReader();
    reader.onloadend = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    setForm((prev) => ({ ...prev, instructorPhoto: null }));
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingInstructor(null);
    setForm(defaultForm);
    setPhotoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("name", form.name);
      formData.append("email", form.email);
      if (form.password) formData.append("password", form.password); // skip if blank in edit mode
      formData.append("number", form.number);
      formData.append("experience", form.experience);
      form.subjects
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean)
        .forEach((subject) => formData.append("subjects[]", subject));
      if (form.instructorPhoto) {
        formData.append("instructorPhoto", form.instructorPhoto);
      }

      if (editingInstructor) {
        // ── PATCH – edit existing instructor ──
        await axiosInstance.patch(
          `${apiUrl.GET_INSTRUCTORS}/${editingInstructor._id}`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } },
        );
      } else {
        // ── POST – add new instructor ──
        await axiosInstance.post(apiUrl.GET_INSTRUCTORS, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      handleCloseForm();
      await fetchInstructors();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save instructor");
    } finally {
      setSubmitting(false);
    }
  };

  const activeInstructors = instructors.filter((i) => i.isActive);
  const isEditMode = !!editingInstructor;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-lg font-semibold text-slate-200">
          Faculty / Instructors
        </h2>
        <button
          type="button"
          onClick={handleAddClick}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-800 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-700"
        >
          <Plus size={16} />
          {showForm && !isEditMode ? "Close form" : "Add instructor"}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="rounded-xl border border-[#1d3350] bg-[#0f1c2e] p-4 shadow-sm space-y-3"
        >
          {/* Form header */}
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-slate-300">
              {isEditMode
                ? `Editing — ${editingInstructor.name}`
                : "New instructor"}
            </p>
            <button
              type="button"
              onClick={handleCloseForm}
              className="text-slate-400 hover:text-slate-400"
            >
              <X size={16} />
            </button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-slate-400">
                Name
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => handleFormChange("name", e.target.value)}
                required
                className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400">
                Email
              </label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => handleFormChange("email", e.target.value)}
                required
                className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-slate-400">
                Phone number
              </label>
              <input
                type="text"
                value={form.number}
                onChange={(e) => handleFormChange("number", e.target.value)}
                required
                className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400">
                Password
                {isEditMode && (
                  <span className="ml-1 text-slate-400 font-normal">
                    (leave blank to keep current)
                  </span>
                )}
              </label>
              <input
                type="password"
                value={form.password}
                onChange={(e) => handleFormChange("password", e.target.value)}
                required={!isEditMode}
                className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-xs font-medium text-slate-400">
                Experience
              </label>
              <input
                type="text"
                value={form.experience}
                onChange={(e) => handleFormChange("experience", e.target.value)}
                required
                className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-400">
                Subjects (comma separated)
              </label>
              <input
                type="text"
                value={form.subjects}
                onChange={(e) => handleFormChange("subjects", e.target.value)}
                required
                className="mt-1 w-full bg-[#0d1220] text-slate-100 placeholder:text-slate-500 rounded-lg border border-[#1d3350] px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500"
              />
            </div>
          </div>

          {/* Instructor Photo */}
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">
              Instructor Photo
            </label>
            <div className="flex items-start gap-4">
              <label
                htmlFor="instructorPhoto"
                className="flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-[#1d3350] bg-[#0d1b2e] px-5 py-4 text-slate-400 transition hover:border-slate-400 hover:text-slate-400"
              >
                <ImagePlus size={22} />
                <span className="text-xs font-medium">
                  {form.instructorPhoto
                    ? "Change photo"
                    : isEditMode
                      ? "Replace photo"
                      : "Upload photo"}
                </span>
                <input
                  id="instructorPhoto"
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="sr-only"
                  onChange={handlePhotoChange}
                />
              </label>

              {photoPreview && (
                <div className="relative">
                  <img
                    src={photoPreview}
                    alt="Instructor preview"
                    className="h-20 w-20 rounded-lg object-cover border border-[#1d3350] shadow-sm"
                  />
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow hover:bg-red-600"
                  >
                    <X size={11} />
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-1.5 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60"
            >
              {submitting && <Loader2 size={16} className="animate-spin" />}
              {isEditMode ? "Update instructor" : "Save instructor"}
            </button>
          </div>
        </form>
      )}

      <section className="space-y-3">
        <h3 className="text-xs font-medium first-letter:uppercase tracking-wide text-slate-500">
          Active instructors ({activeInstructors.length})
        </h3>
        {loading ? (
          <div className="flex items-center justify-center rounded-lg border border-[#1d3350] bg-[#0f1c2e] py-10">
            <Loader2 size={24} className="animate-spin text-slate-400" />
          </div>
        ) : error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-xs text-red-700">
            {error}
          </div>
        ) : instructors.length === 0 ? (
          <div className="rounded-lg border border-[#1d3350] bg-[#0f1c2e] px-4 py-6 text-center text-sm text-slate-500">
            No instructors yet. Use "Add instructor" to create one.
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-[#1d3350] bg-[#0f1c2e] shadow-sm">
            <div className="overflow-x-auto">
              <table className="min-w-full text-left text-sm">
                <thead className="bg-[#0d1b2e]">
                  <tr>
                    <th className="px-4 py-2 text-xs font-semibold tracking-wide text-slate-500">
                      Name
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold tracking-wide text-slate-500">
                      Email
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold tracking-wide text-slate-500">
                      Phone
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold tracking-wide text-slate-500">
                      Experience
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold tracking-wide text-slate-500">
                      Subjects
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold tracking-wide text-slate-500">
                      Active
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold tracking-wide text-slate-500">
                      Batches status
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold tracking-wide text-slate-500">
                      Edit
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold tracking-wide text-slate-500">
                      delete
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {instructors.map((inst) => (
                    <tr
                      key={inst._id}
                      className={
                        editingInstructor?._id === inst._id ? "bg-[#0d1b2e]" : ""
                      }
                    >
                      <td className="px-4 py-2 text-slate-200">{inst.name}</td>
                      <td className="px-4 py-2 text-slate-400">{inst.email}</td>
                      <td className="px-4 py-2 text-slate-400">
                        {inst.number}
                      </td>
                      <td className="px-4 py-2 text-slate-400">
                        {inst.experience}
                      </td>
                      <td className="px-4 py-2 text-slate-400">
                        {Array.isArray(inst.subjects)
                          ? inst.subjects.join(", ")
                          : ""}
                      </td>
                      <td className="px-4 py-2">
                        <button
                          type="button"
                          onClick={() => handleToggleActive(inst)}
                          disabled={savingId === inst._id}
                          className="inline-flex items-center gap-1 text-slate-300 hover:text-slate-100 disabled:opacity-60"
                        >
                          {savingId === inst._id ? (
                            <Loader2
                              size={16}
                              className="animate-spin text-slate-400"
                            />
                          ) : inst.isActive ? (
                            <ToggleRight
                              size={20}
                              className="text-emerald-500"
                            />
                          ) : (
                            <ToggleLeft size={20} className="text-slate-300" />
                          )}
                          <span className="text-xs">
                            {inst.isActive ? "Active" : "Inactive"}
                          </span>
                        </button>
                      </td>
                      <td className="px-4 py-2">
                        <Link
                          to={`/batch-management?instructorId=${inst._id}&instructorName=${inst.name}&status=${inst.isActive ? "active" : "inactive"}`}
                          className="inline-flex items-center gap-1 text-slate-300 hover:text-slate-100"
                        >
                          <EyeIcon size={16} />
                          <span className="text-xs">view</span>
                        </Link>
                      </td>
                      <td className="px-4 py-2">
                        <button
                          type="button"
                          onClick={() => handleEditClick(inst)}
                          className={`inline-flex items-center gap-1 transition-colors hover:text-slate-100 ${
                            editingInstructor?._id === inst._id
                              ? "text-slate-100"
                              : "text-slate-400 hover:text-slate-300"
                          }`}
                          title="Edit instructor"
                        >
                          <Pencil size={15} />
                        </button>
                      </td>
                      <td className="px-4 py-2">
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(inst._id)}
                          className={`inline-flex items-center gap-1 transition-colors hover:text-slate-100 ${
                            editingInstructor?._id === inst._id
                              ? "text-slate-100"
                              : "text-slate-400 hover:text-slate-300"
                          }`}
                          title="Edit instructor"
                        >
                          <Trash2 color="red" size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}

export default FacultyManagement;